import {
  Box,
  CardContent,
  FormControl,
  Grid,
  MenuItem,
  Select,
  Typography,
  Button,
} from '@mui/material';
import DashboardTimeRange from '../DashboardTimeRange';
import { DASHBOARD_TIME_BUTTONS } from '@bumble/app/constants';
import { useEffect, useState } from 'react';
import { DateTime, DurationLikeObject } from 'luxon';
import { toast } from 'react-toastify';
import { Loading } from '../Loading';
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  Legend,
} from 'recharts';
import { useApi } from '@bumble/app/hooks/use-api';
import { useQuery, useMutation } from '@tanstack/react-query';
import { getTooltipLabel } from '@bumble/app/utils/get-date';
import PathVisualization from './PathVisualization';
import DataTableGrid from '../DataTableGrid';
import { GridColDef } from '@mui/x-data-grid';
import { apiError } from '@bumble/app/utils/api-error';
import { useCpe } from './CpeContext';
import DialogStandard from '../DialogStandard';
import { PostAnalysisData } from './PostAnalysisData';

interface StatusDataRow {
  node_id: string;
  '@timestamp': string;
  timestamp: string;
  gw_pingable: string;
  gw_pingable_raw: string;
  status: string;
  type: string;
}

interface WANLinkHealthDashboardProps {
  selectedTab: string;
}

export const WANLinkHealthDashboard = ({ selectedTab }: WANLinkHealthDashboardProps) => {
  const [timeRange, setTimeRange] = useState<DurationLikeObject>(DASHBOARD_TIME_BUTTONS[0].value);
  const { cpe, setCpe } = useCpe();
  const [healthChardData, setHealthChartData] = useState<any[]>([]);
  const [rttChartData, setRttChartData] = useState<any[]>([]);
  const [lossChartData, setLossChartData] = useState<any[]>([]);
  const [upTimePercent, setUpTimePercent] = useState<string>('');
  // The uptime accounting anchor (conn_status_start_at). When set, the
  // "Set Start Time" button is disabled: the start time is write-once.
  const [connStatusStartAt, setConnStatusStartAt] = useState<string | null>(null);
  const [statusTableData, setStatusTableData] = useState<any[]>([]);
  const [postModalOpen, setPostModalOpen] = useState(false);
  const [postModalData, setPostModalData] = useState<any>(null);
  const [rttSetting, setRttSetting] = useState<'average' | 'max'>('average');

  const api = useApi();
  const colors = ['#6E9FCE', '#C5904A', '#5F467E'];
  const rttSettings = [
    { value: 'average', label: 'Average' },
    { value: 'max', label: 'Max' },
  ];

  const { isLoading, data, error, refetch, fetchStatus, isRefetching } = useQuery(
    ['graphdata'],
    api.dashboardGetNodeWanLinkHealthApiV1DashboardGetNodeWanLinkHealthGet.bind(null, {
      node_id: cpe.nodeId,
      node_name: cpe.cpeName,
      start_time: DateTime.now().minus(timeRange).toISO(),
      end_time: DateTime.now().toISO(),
    }),
    { enabled: false, onError: apiError },
  );

  const {
    isLoading: statusLoading,
    data: statusData,
    error: statusError,
    refetch: statusRefetch,
    fetchStatus: statusFetchStatus,
    isRefetching: statusRefetching
  } = useQuery(
    ['statusdata'],
    api.getNodeConnStatusApiV1GetNodeConnStatusGet.bind(null, { node_id: cpe.nodeId }),
    { enabled: false, retry: false, onError: apiError },
  );

  const postAnalysisApi = useMutation(api.getNodeConnPostMortemApiV1GetNodeConnPostMortemGet, {
    onSuccess: async (data) => {
      const reader = data.body?.getReader();
      const decoder = new TextDecoder();
      if (!reader) return;
      let { value, done } = await reader.read();
      let result = '';
      while (!done) {
        const chunk = decoder.decode(value, { stream: true });
        if (!chunk) continue;
        result += chunk + '\n';
        ({ value, done } = await reader.read());
      }
      setPostModalData(result);
      setPostModalOpen(true);
    },
    onError: apiError
  });

  const resetConnStatusStart = useMutation(
    api.resetCpeConnStatusStartApiV1ResetCpeConnStatusStartPost,
    {
      onSuccess: () => {
        toast.success('Uptime start time set to now.');
        // Refetch so the returned conn_status_start_at (now set) disables the button.
        statusRefetch();
      },
      onError: apiError,
    },
  );

  const handleResetStartTimeClick = () => {
    resetConnStatusStart.mutate({ cpe_id: cpe.nodeId });
  };

  const handlePostAnalysisClick = (row: StatusDataRow) => {
    postAnalysisApi.mutate({
      node_id: row.node_id,
      timestamp: row.timestamp,
      rec_type: row.type,
      status: row.status,
      gw_pingable: row.gw_pingable_raw,
    });
  };

  const columns: GridColDef[] = [
    {
      field: '@timestamp',
      headerName: 'Timestamp',
      width: 300,
      disableColumnMenu: true,
    }, {
      field: 'status',
      headerName: 'Status',
      width: 200,
      disableColumnMenu: true,
    }, {
      field: 'gw_pingable',
      headerName: 'Self Diagnosis',
      minWidth: 200,
      disableColumnMenu: true,
    }, {
      field: 'post_analysis',
      headerName: 'Post-mortem Analysis',
      flex: 1,
      minWidth: 300,
      renderCell: (params) => (
        (params.row.status === 'down' || params.row.status === 'wan_link_down') ? (
          <Button
            variant="outlined"
            size="small"
            onClick={() => handlePostAnalysisClick(params.row)}
          >
            View Analysis
          </Button>
        ) : null
      ),
      disableColumnMenu: true,
    }
  ];

  const handleTimeChange = (e: React.MouseEvent<HTMLElement>, val: DurationLikeObject) => {
    if (val) {
      setTimeRange(val);
    }
  };

  const handleCpeChange = (val: any) => {
    if (val) {
      setCpe({
        cpeName: val.label,
        nodeId: val.id,
      });
    }
  };

  useEffect(() => {
    if (cpe.cpeName && timeRange) {
      setStatusTableData([]);
      setUpTimePercent('');
      setConnStatusStartAt(null);
      refetch();
      statusRefetch();
    }
  }, [timeRange, cpe.cpeName])

  useEffect(() => {
    if (data?.data && !error) {
      setHealthChartData(data.data.map(n => ({
        timestamp: new Date(n.timestamp).getTime(),
        health: n.health,
      })).sort((a: any, b: any) => a.timestamp - b.timestamp))
      setRttChartData(data.data.map(n => ({
        timestamp: new Date(n.timestamp).getTime(),
        rtt_ms: n.RTT_ms,
        max_rtt: n.max_rtt,
        avg_jitter: n.avg_jitter,
      })).sort((a: any, b: any) => a.timestamp - b.timestamp))
      setLossChartData(data.data.map(n => ({
        timestamp: new Date(n.timestamp).getTime(),
        loss_rate: n['Loss Rate'],
      })).sort((a: any, b: any) => a.timestamp - b.timestamp))
    }
  }, [data])

  useEffect(() => {
    if (statusData?.data && !statusError) {
      setUpTimePercent(`${statusData.data.percent}`);
      setConnStatusStartAt(statusData.data.conn_status_start_at ?? null);
      setStatusTableData((statusData.data.history as StatusDataRow[]).map(h => {
        const origVal = h.gw_pingable;
        const displayVal = h.status === 'up'
          ? 'Normal'
          : (origVal.toLocaleLowerCase() === 'yes'
              ? 'Last mile problem'
              : (origVal.toLocaleLowerCase() === 'no' ? 'Middle mile problem' : 'Undetermined')
            );
        return {
          ...h,
          id: `${h.node_id}-${h['@timestamp']}`,
          gw_pingable: displayVal,
          gw_pingable_raw: origVal,
        }
      }));
    }
  }, [statusData, statusError])

  const handleRefreshClick = () => {
    refetch();
    statusRefetch();
    toast.success('Refreshed chart data.');
  };

  const getTickFormatter = () => {
    const totalHours = timeRange.hours || (timeRange.days || 0) * 24;

    if (totalHours <= 3) {
      return (v: number) => DateTime.fromMillis(v).toFormat('HH:mm');
    } else {
      return undefined;
    }
  };

  const getEvenlySpacedTicks = (data: any[]) => {
    if (!data || data.length === 0) return [];

    const timestamps = data.map(d => d.timestamp);
    const min = Math.min(...timestamps);
    const max = Math.max(...timestamps);
    const range = max - min;

    const totalHours = timeRange.hours || (timeRange.days || 0) * 24;
    let numTicks = 5;

    if (totalHours <= 3) {
      numTicks = 4;
    } else if (timeRange.days === 1) {
      numTicks = 6;
    } else {
      numTicks = 7;
    }

    const ticks: number[] = [];
    for (let i = 0; i < numTicks; i++) {
      const tickValue = min + (range / (numTicks - 1)) * i;
      ticks.push(tickValue);
    }

    return ticks;
  };

  function CustomizedTicksWithDateTime(props: any) {
    const { x, y, payload } = props;
    const date = DateTime.fromMillis(payload.value);
    return (
      <g transform={`translate(${x},${y})`}>
        <text x={0} y={0} dy={16} fill="#666">
          <tspan textAnchor="middle" x="0">
            {date.toFormat('MMM d')}
          </tspan>
          <tspan textAnchor="middle" x="0" dy="20">
            {date.toFormat('HH:mm')}
          </tspan>
        </text>
      </g>
    );
  }

  function CustomizedTicksForChartData(props: any) {
    const { x, y, payload } = props;
    const date = DateTime.fromISO(payload.value);

    return (
      <g transform={`translate(${x},${y})`}>
        <text x={0} y={0} dy={16} fill="#666">
          <tspan textAnchor="middle" x="0">
            {date.toFormat('MMM d')}
          </tspan>
          <tspan textAnchor="middle" x="0" dy="20">
            {date.toFormat('HH:mm')}
          </tspan>
        </text>
      </g>
    );
  }

  return (
    <Box>
      {upTimePercent ? (
        <>
          <Typography sx={{
            position: 'absolute',
            top: 182,
            left: 720,
            maxWidth: 200,
          }}>
            Uptime{' '}
          </Typography>
          <Typography sx={{
            position: 'absolute',
            top: 170,
            left: 780,
            maxWidth: 200,
          }}>
            <span style={{
              color: 'rgb(31, 207, 57)',
              fontSize: '2em',
              fontWeight: 'bold',
            }}>
              {upTimePercent}
            </span>
            <span style={{ color: 'rgb(31, 207, 57)' }}>%</span>
          </Typography>
          <Button
            variant="outlined"
            size="small"
            onClick={handleResetStartTimeClick}
            // One-time initialization: the start time is write-once, so once it
            // is set the action is done and the button stays disabled.
            disabled={Boolean(connStatusStartAt) || resetConnStatusStart.isLoading}
            sx={{
              position: 'absolute',
              top: 176,
              left: 930,
            }}
          >
            Set Start Time
          </Button>
        </>
      ) : ''}
      <Box sx={{ mb: 0, py: 5 }}>
        <DashboardTimeRange
          timeValues={DASHBOARD_TIME_BUTTONS}
          selectedTime={timeRange}
          onCpeChange={handleCpeChange}
          onTimeChange={handleTimeChange}
          onRefresh={handleRefreshClick}
          selectedTab={selectedTab}
        />
      </Box>
      <CardContent sx={{bgcolor: '#fff', mb: 5}}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={12}>
            <Box>
              <Typography color="primary" sx={{ mb: 2 }}>WAN Link Health (%)</Typography>
              {((isLoading || isRefetching) && fetchStatus !== 'idle') ?
              <Loading height={200} /> :
              <ResponsiveContainer width="100%" height={200}>
                <LineChart
                  margin={{ top: 5, right: 10, left: 10, bottom: 5, }}
                  data={healthChardData}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="timestamp"
                    type="number"
                    scale="time"
                    domain={['dataMin', 'dataMax']}
                    ticks={getEvenlySpacedTicks(healthChardData)}
                    tickFormatter={getTickFormatter()}
                    tick={getTickFormatter() === undefined ? <CustomizedTicksWithDateTime /> : undefined}
                    height={getTickFormatter() === undefined ? 60 : undefined}
                  />
                  <YAxis
                    orientation="left"
                    type="number"
                    domain={[0, 100]}
                    width={30}
                    tickFormatter={(value: number) => new Intl.NumberFormat('en-US', {
                      notation: 'compact',
                      compactDisplay: 'short',
                    }).format(value)}
                  />
                  <Line strokeWidth={2} stroke={colors[0]} dot={false} dataKey="health" name="WAN Link Health" />
                  <Tooltip labelFormatter={(label) => getTooltipLabel(label)} />
                </LineChart>
              </ResponsiveContainer>}
            </Box>
          </Grid>
        </Grid>
      </CardContent>
      <Grid container spacing={5} mb={5}>
        <Grid item sm={12} md={6}>
          <CardContent sx={{ bgcolor: '#fff', borderRadius: 1.2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography color="primary">WAN Link RTT (ms)</Typography>
                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <Select
                    value={rttSetting}
                    onChange={(e) => setRttSetting(e.target.value as 'average' | 'max')}
                    sx={(theme) => ({
                      height: 32,
                      '& .MuiOutlinedInput-notchedOutline': { borderColor: `${theme.palette.divider} !important` },
                      '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: `${theme.palette.divider} !important` },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: `${theme.palette.divider} !important` },
                    })}
                  >
                    {rttSettings.map(({ value, label }) => (
                      <MenuItem key={value} value={value}>{label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
              <Typography color="primary">WAN Link Jitter (ms)</Typography>
            </Box>
            {((isLoading || isRefetching) && fetchStatus !== 'idle') ? <Loading height={200} /> :
            <ResponsiveContainer width="100%" height={200}>
              <LineChart
                margin={{ top: 5, right: 10, left: 10, bottom: 5, }}
                data={rttChartData}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="timestamp"
                  type="number"
                  scale="time"
                  domain={['dataMin', 'dataMax']}
                  ticks={getEvenlySpacedTicks(rttChartData)}
                  tickFormatter={getTickFormatter()}
                  tick={getTickFormatter() === undefined ? <CustomizedTicksWithDateTime /> : undefined}
                  height={getTickFormatter() === undefined ? 60 : undefined}
                />
                <YAxis
                  yAxisId="left"
                  orientation="left"
                  type="number"
                  domain={rttChartData.length > 0 ? undefined : [0, 10]}
                  width={30}
                  tickFormatter={(value: number) => new Intl.NumberFormat('en-US', {
                    notation: 'compact',
                    compactDisplay: 'short',
                  }).format(value)}
                />
                <YAxis
                  yAxisId="right"
                  orientation="right"
                  type="number"
                  domain={rttChartData.length > 0 ? undefined : [0, 10]}
                  width={30}
                  tickFormatter={(value: number) => new Intl.NumberFormat('en-US', {
                    notation: 'compact',
                    compactDisplay: 'short',
                  }).format(value)}
                />
                <Line
                  yAxisId="left"
                  strokeWidth={2}
                  stroke={colors[1]}
                  dot={false}
                  dataKey={rttSetting === 'max' ? 'max_rtt' : 'rtt_ms'}
                  name={rttSetting === 'max' ? 'WAN Link Max Round Trip Time' : 'WAN Link Round Trip Time'}
                />
                <Line
                  yAxisId="right"
                  strokeWidth={2}
                  stroke="#7EB670"
                  dot={false}
                  dataKey="avg_jitter"
                  name="WAN Link Jitter"
                />
                <Tooltip labelFormatter={(label) => getTooltipLabel(label)} />
                <Legend wrapperStyle={{ bottom: -5 }} iconType={'rect'} />
              </LineChart>
            </ResponsiveContainer>}
          </CardContent>
        </Grid>
        <Grid item sm={12} md={6}>
          <CardContent sx={{ bgcolor: '#fff', borderRadius: 1.2 }}>
            <Typography color="primary" sx={{ mb: 2 }}>WAN Link Packet Loss Rate (%)</Typography>
            {((isLoading || isRefetching) && fetchStatus !== 'idle') ? <Loading height={200} /> :
            <ResponsiveContainer width="100%" height={200}>
              <LineChart
                margin={{ top: 5, right: 10, left: 10, bottom: 5, }}
                data={lossChartData}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="timestamp"
                  type="number"
                  scale="time"
                  domain={['dataMin', 'dataMax']}
                  ticks={getEvenlySpacedTicks(lossChartData)}
                  tickFormatter={getTickFormatter()}
                  tick={getTickFormatter() === undefined ? <CustomizedTicksWithDateTime /> : undefined}
                  height={getTickFormatter() === undefined ? 60 : undefined}
                />
                <YAxis
                  orientation="left"
                  type="number"
                  domain={[0, 100]}
                  width={30}
                  tickFormatter={(value: number) => new Intl.NumberFormat('en-US', {
                    notation: 'compact',
                    compactDisplay: 'short',
                  }).format(value)}
                />
                <Line strokeWidth={2} stroke={colors[2]} dot={false} dataKey="loss_rate" name="WAN Link Packet Loss Rate" />
                <Tooltip labelFormatter={(label) => getTooltipLabel(label)} />
              </LineChart>
            </ResponsiveContainer>}
          </CardContent>
        </Grid>
      </Grid>
      <CardContent sx={{bgcolor: '#fff', mb: 5}}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={12}>
            <Box>
              <Typography color="primary" sx={{ mb: 2 }}>Outage History</Typography>
              {((statusLoading || statusRefetching) && statusFetchStatus !== 'idle' && !statusError) ?
                <Loading height={300} /> :
                <Box sx={{ height: 300, overflow: 'auto' }}>
                  <DataTableGrid
                    rows={statusTableData}
                    columns={columns}
                    pagination={true}
                  />
                </Box>}
            </Box>
          </Grid>
        </Grid>
      </CardContent>
      <CardContent sx={{bgcolor: '#fff', mb: 5}}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={12}>
            <Box>
              <Typography color="primary" sx={{ mb: 2 }}>Path Visualization</Typography>
              {((isLoading || isRefetching) && fetchStatus !== 'idle') ? <Loading height={200} /> :
              <ResponsiveContainer width="100%" height={200}>
                <PathVisualization
                  nodeId={cpe.nodeId}
                  nodeName={cpe.cpeName}
                />
              </ResponsiveContainer>}
            </Box>
          </Grid>
        </Grid>
      </CardContent>
      <DialogStandard
        size="md"
        open={postModalOpen}
        handleClose={() => setPostModalOpen(false)}
        title="Post-Mortem Analysis"
      >
        <PostAnalysisData
          onClose={() => setPostModalOpen(false)}
          data={postModalData}
        />
      </DialogStandard>
    </Box>
  );
};
